import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import './CompanyAutocomplete.scss';

interface CompanySuggestion {
  id: number;
  name: string;
  company_code?: string;
  city?: string;
  activity_sector?: string;
}

interface CompanyAutocompleteProps {
  value: string;
  companyCode: string;
  onCompanySelect: (name: string, code: string, activitySector?: string) => void;
  onManualInput: (name: string) => void;
  className?: string;
  error?: boolean;
  placeholder?: string;
  id?: string;
}

const CompanyAutocomplete: React.FC<CompanyAutocompleteProps> = ({
  value,
  companyCode,
  onCompanySelect,
  onManualInput,
  className = '',
  error = false,
  placeholder = 'Ieškokite įmonės pagal pavadinimą arba kodą',
  id,
}) => {
  const [suggestions, setSuggestions] = useState<CompanySuggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFromList, setSelectedFromList] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await axios.get(`${apiUrl}/companies/autocomplete`, {
        params: { q: query, limit: 10 },
      });
      if (response.data?.success) {
        setSuggestions(response.data.data);
        setShowDropdown(response.data.data.length > 0);
        setHighlightedIndex(-1);
      }
    } catch (err) {
      console.error('Company autocomplete error:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSelectedFromList(false);
    onManualInput(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250);
  };

  const handleSelect = (suggestion: CompanySuggestion) => {
    setSelectedFromList(true);
    setSuggestions([]);
    setShowDropdown(false);
    onCompanySelect(
      suggestion.name,
      suggestion.company_code || '',
      suggestion.activity_sector || ''
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="company-autocomplete" ref={wrapperRef}>
      <input
        ref={inputRef}
        type="text"
        id={id}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0 && !selectedFromList) {
            setShowDropdown(true);
          }
        }}
        className={`${className} ${error ? 'error' : ''}`}
        placeholder={placeholder}
        autoComplete="off"
      />
      {loading && <span className="autocomplete-spinner" />}

      {selectedFromList && companyCode && (
        <div className="selected-company-badge">
          Kodas: {companyCode}
          <button
            type="button"
            onClick={() => {
              setSelectedFromList(false);
              onManualInput(value);
              if (inputRef.current) inputRef.current.focus();
            }}
            title="Keisti įmonę"
          >
            &times;
          </button>
        </div>
      )}

      {showDropdown && suggestions.length > 0 && (
        <div className="autocomplete-dropdown">
          <ul className="autocomplete-items">
            {suggestions.map((s, i) => (
              <li
                key={s.id}
                className={`autocomplete-item ${i === highlightedIndex ? 'highlighted' : ''}`}
                onMouseDown={() => handleSelect(s)}
                onMouseEnter={() => setHighlightedIndex(i)}
              >
                <span className="company-name">{s.name}</span>
                <span className="company-meta">
                  {s.company_code && <span className="company-code">{s.company_code}</span>}
                  {s.city && <span className="company-city">{s.city}</span>}
                </span>
              </li>
            ))}
          </ul>
          <div className="autocomplete-hint">
            Nerandate? Tiesiog įveskite įmonės pavadinimą
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyAutocomplete;
